import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  Event as RouterNavigationEvent,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { filter, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { UserAPIModelsERoles } from 'user-ts-ng-client';

import { IconComponent } from '@shared/components/icon/icon.component';
import { LogoComponent } from '@shared/components/logo/logo.component';
import { IconConfig } from '@shared/models/icon.model';
import { AuthService } from '@core/auth/auth.service';
import { UserDataService } from '@core/auth/user-data.service';
import { OrganizationService } from '@features/organization/services/organization.service';
import { UserService } from '@features/organization/services/user.service';

@Component({
  selector: 'srm-dashboard-sidebar',
  imports: [RouterLink, RouterLinkActive, LogoComponent, IconComponent],
  templateUrl: './dashboard-sidebar.component.html',
  styleUrl: './dashboard-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class DashboardSidebarComponent implements AfterViewInit, OnInit, OnDestroy {
  private static readonly DESKTOP_BREAKPOINT = 800;
  private static readonly STORAGE_KEY = 'srm.dashboard.sidebar.open';
  private readonly cdr = inject(ChangeDetectorRef);

  private authService = inject(AuthService);
  private userDataService = inject(UserDataService);
  private organizationService = inject(OrganizationService);
  private userService = inject(UserService);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private destroy$ = new Subject<void>();

  public userRole: UserAPIModelsERoles | undefined;
  public roles = UserAPIModelsERoles;
  public projectsAllowedRoles: UserAPIModelsERoles[] = [
    UserAPIModelsERoles.GlobalAdmin,
    UserAPIModelsERoles.SrmManager,
    UserAPIModelsERoles.SrmContributor,
  ];
  public trackersAllowedRoles: UserAPIModelsERoles[] = [
    UserAPIModelsERoles.GlobalAdmin,
    UserAPIModelsERoles.SrmManager,
    UserAPIModelsERoles.SrmContributor,
  ];
  public organizationAllowedRoles: UserAPIModelsERoles[] = [
    UserAPIModelsERoles.GlobalAdmin,
    UserAPIModelsERoles.SrmManager,
    UserAPIModelsERoles.OrganizationAdmin,
    UserAPIModelsERoles.SrmContributor,
  ];
  public invoicesAllowedRoles: UserAPIModelsERoles[] = [
    UserAPIModelsERoles.GlobalAdmin,
    UserAPIModelsERoles.SrmManager,
    UserAPIModelsERoles.SrmContributor,
  ];
  public organizationId: string | undefined;
  public canViewOrganization = false;
  public canViewInvoices = false;
  public canViewProjects = false;
  public canViewTrackers = false;
  public canViewContractAdvisor = false;
  public isInternalUser = false;
  public sidebarRef = inject(ElementRef<HTMLElement>);
  public dashboardElement: HTMLElement | null = null;
  public sidebarElement: HTMLElement | null = null;
  public isSidebarOpen: boolean;
  public iconConfig: IconConfig = {
    url: 'assets/icons/bar-chart-square.svg',
    alt: 'Navigation Item',
    style: {
      width: '24px',
      height: '24px',
    },
    class: 'sidebar-nav-icon',
  };

  public homeIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroHome',
    alt: 'Home',
  };

  public accountIconConfig: IconConfig = {
    ...this.iconConfig,
    url: 'assets/icons/user.svg',
  };

  public menuIconConfig: IconConfig = {
    ...this.iconConfig,
    url: 'assets/icons/menu.svg',
  };

  public searchIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroMagnifyingGlass',
    alt: 'Search App',
  };

  public advisorIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroDocumentCheck',
    alt: 'ContractAdvisor',
  };

  public contractIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroDocument',
    alt: 'Contracts',
  };

  public trackerIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroChartBar',
    alt: 'Trackers',
  };

  public alertIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroBell',
    alt: 'Notifications',
  };

  public orgIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroCog6Tooth',
    alt: 'Account Settings',
  };

  public organizationsIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroBuildingOffice',
    alt: 'Organizations',
  };

  public projectsIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroFolder',
    alt: 'Engagements',
  };

  public invoicesIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroSquare3Stack3d',
    alt: 'Invoices',
  };

  public logoutIconConfig: IconConfig = {
    ...this.iconConfig,
    iconName: 'heroArrowLeftOnRectangle',
    alt: 'Logout',
  };

  public constructor() {
    this.isSidebarOpen = this.isDesktop() ? this.getStoredDesktopState() : false;
  }

  public ngOnInit(): void {
    this.userRole = this.authService.getRole();
    this.organizationId = this.userDataService.getOrganizationId();
    this.isInternalUser = this.authService.isInternalUser();
    this.canViewProjects = !!this.userRole && this.projectsAllowedRoles.includes(this.userRole);
    this.canViewTrackers = !!this.userRole && this.trackersAllowedRoles.includes(this.userRole);
    this.canViewInvoices = !!this.userRole && this.invoicesAllowedRoles.includes(this.userRole);
    this.canViewOrganization =
      !!this.userRole && this.organizationAllowedRoles.includes(this.userRole);

    if (this.organizationId) {
      this.organizationService
        .watchContractAdvisorSubscription(this.organizationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((isSubscribed) => {
          this.canViewContractAdvisor = !this.isInternalUser && isSubscribed;
          this.cdr.markForCheck();
        });
    }

    this.router.events
      .pipe(
        filter(
          (event: RouterNavigationEvent): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (!this.isDesktop()) {
          this.setSidebarOpen(false);
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngAfterViewInit(): void {
    this.dashboardElement = (this.sidebarRef.nativeElement as HTMLElement).closest(
      '.srm-template--dashboard',
    );

    this.sidebarElement = (this.sidebarRef.nativeElement as HTMLElement).querySelector('.sidebar');
    this.syncSidebarClasses();
  }

  public toggleSidebar(): void {
    this.setSidebarOpen(!this.isSidebarOpen);

    if (this.isDesktop()) {
      this.saveDesktopState();
    }
  }

  public async onLogout(): Promise<void> {
    try {
      if (this.authService.getImpersonationState()) {
        await firstValueFrom(this.userService.stopImpersonation());
      }
    } catch (error) {
      console.warn('Failed to stop impersonation before logout', error);
    }

    await this.authService.logout();
  }

  private setSidebarOpen(isOpen: boolean): void {
    this.isSidebarOpen = isOpen;
    this.syncSidebarClasses();
  }

  private syncSidebarClasses(): void {
    this.sidebarElement?.classList.toggle('sidebar--open', this.isSidebarOpen);
    this.dashboardElement?.classList.toggle('sidebar--open', this.isSidebarOpen);
  }

  private isDesktop(): boolean {
    return (
      (this.document.defaultView?.innerWidth ?? 0) >= DashboardSidebarComponent.DESKTOP_BREAKPOINT
    );
  }

  private getStoredDesktopState(): boolean {
    try {
      const storedValue = this.document.defaultView?.localStorage.getItem(
        DashboardSidebarComponent.STORAGE_KEY,
      );

      return storedValue === 'false' ? false : true;
    } catch {
      return true;
    }
  }

  private saveDesktopState(): void {
    try {
      this.document.defaultView?.localStorage.setItem(
        DashboardSidebarComponent.STORAGE_KEY,
        String(this.isSidebarOpen),
      );
    } catch {
      // Storage is optional; navigation remains usable when it is unavailable.
    }
  }
}
